/* eslint-disable */
import { DashboardStore } from '../stores/dashboard';
import {
    UnitTypeValues,
    abortSelectedFetch,
    cleanCompaniesStore,
    getCardSetsSelect,
    getCatalogsOriginalUnitTypeSelect,
    getCompaniesSelect,
    getOperatorsSelect,
} from './';
import dayjs from 'dayjs';
import { appConfig } from '../settings';
import { apiManager } from '../REST';
import {
    ICardSetRevenueItem,
    ICardsRevenueItem,
    ICardsUsage,
    ICompanyRevenueItem,
    ICompanyUsage,
    ICountryRevenueItem,
    IDashboardType,
    IFinancesRollAPI,
    IFinancesRollCards,
    IGetApiType,
    IOperatorRevenueItem,
    IOverlayView,
    IParamsList,
    IRankCardRevenueItemAPI,
    IRankCardSetRevenueItemAPI,
    IRankCompanyRevenueItemAPI,
    IRankCountryRevenueItemAPI,
    IRankOperatorRevenueItemAPI,
    IRankTimelineRevenueItemAPI,
    IRankZoneRevenueItemAPI,
    IStatItem,
    ITelecomUsage,
    ITimelineRevenueItem,
    ITimelineUsage,
    ITimelineZoneItem,
    IUsageActivityRoll,
    IUsageActivityRollAPI,
    IUsageData,
    IUsageMap,
    IZoneUsage,
} from '../interfaces';
import {
    getApiTypeWithUnitType,
    mapApi2UiField,
    mapApi2UiList,
    mapRank2RevenueItem,
    mapStatApiItems,
    sortByString,
} from '../instruments';

function setDashboardActivityUsage(data: IUsageActivityRoll): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setUsageActivity(data);
}

export async function getActivityRollAsExternal(params: IParamsList = {}): Promise<void> {
    const { resp } = await apiManager.getApi('GET_ACTIVITY_ROLL', { searchParamsList: params });
    const respUsageActivityRoll = resp as IUsageActivityRollAPI;
    setDashboardActivityUsage({
        ...mapApi2UiField(respUsageActivityRoll),
        costs: mapStatApiItems(respUsageActivityRoll.costs),
        statsByUnitType: mapApi2UiList(respUsageActivityRoll.stats_by_unit_type),
    });
}

export const getDashboardActivityRoll = async (): Promise<void> => {
    const response = await apiManager.getApi('GET_ACTIVITY_ROLL');
    const resp = response.resp as IUsageActivityRollAPI;
    setDashboardActivityUsage({
        ...mapApi2UiField(resp),
        costs: mapStatApiItems(resp.costs),
        statsByUnitType: mapApi2UiList(resp.stats_by_unit_type),
    });
};

function setDashboardFinancesRollCards(cards: IFinancesRollCards): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setFinancesRollCards(cards);
}
function setDashboardFinancesRollStats(data: Array<IStatItem>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setFinancesRollStats(data);
}

function setDashboardFinancesRollVolume(volumes: string): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setFinancesRollVolume(volumes);
}

export async function getDashboardFinancesUsage(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_FINANCE_ROLL', { searchParamsList: params });
    const { cards, costs_and_revenues, volume } = resp as IFinancesRollAPI;
    setDashboardFinancesRollCards({ active: cards?.active || 0, inactive: cards?.inactive || 0 });
    setDashboardFinancesRollStats(mapStatApiItems(costs_and_revenues));
    setDashboardFinancesRollVolume(volume ? volume : '0');
}

// ------------ CountryUsage ------------
export async function getDashboardCountryUsageList(params: IParamsList): Promise<void> {
    const unitType = params?.unitType || UnitTypeValues.DATA;
    const usageDataName = getApiTypeWithUnitType(`GET_USAGE_COUNTRY_LIST`, unitType);
    const { resp } = await apiManager.getApi(
        'GET_USAGE_COUNTRY_LIST',
        { searchParamsList: params },
        undefined,
        undefined,
        usageDataName,
    );

    setDashboardUsageData(usageDataName, resp as Array<IUsageData>);
}

// ------------ CardSetUsage ------------
export async function getDashboardCardSetUsageList(params: IParamsList): Promise<void> {
    const unitType = params?.unitType || UnitTypeValues.DATA;
    const usageDataName = getApiTypeWithUnitType(`GET_USAGE_CARD_SETS_LIST`, unitType);
    const { resp } = await apiManager.getApi(
        'GET_USAGE_CARD_SETS_LIST',
        { searchParamsList: params },
        undefined,
        undefined,
        usageDataName,
    );
    const data = resp as Array<IUsageData>;
    const ranks =
        data
            .sort((a: IUsageData, b: IUsageData) => Number(b.volume) - Number(a.volume))
            .filter((_: ICardsUsage, index: number) => index < appConfig.top5) || [];
    setDashboardUsageData(usageDataName, ranks);
}

// ------------ Cards Usage ------------
export async function getDashboardCardsUsageList(params: IParamsList): Promise<void> {
    const unitType = params?.unitType || UnitTypeValues.DATA;
    const usageDataName = getApiTypeWithUnitType(`GET_USAGE_CARDS_LIST`, unitType);
    const { resp } = await apiManager.getApi(
        'GET_USAGE_CARDS_LIST',
        { searchParamsList: params },
        undefined,
        undefined,
        usageDataName,
    );

    const data = resp as Array<IUsageData>;
    const ranks =
        data
            .sort((a: IUsageData, b: IUsageData) => Number(b.volume) - Number(a.volume))
            .filter((_: ICardsUsage, index: number) => index < appConfig.top10) || [];

    setDashboardUsageData(usageDataName, ranks);
}

// ------------ Timeline Usage ------------
function setDashboardTimelineUsageList(data: Array<ITimelineUsage>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setTimelineUsageList(data);
}

export async function getDashboardTimelineUsageList(params: IParamsList = {}): Promise<void> {
    const { resp } = await apiManager.getApi('GET_USAGE_TIMELINE_LIST', { searchParamsList: params });
    const data = resp as Array<IUsageData>;
    const ranks = data.sort((a: IUsageData, b: IUsageData) => +dayjs(a.start_date) - +dayjs(b.start_date)) || [];
    setDashboardTimelineUsageList(ranks);
}

// ------------ CompanyUsage ------------
function setDashboardCompanyUsageList(data: Array<ICompanyUsage>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setCompanyUsageList(data);
}

export async function getDashboardCompanyUsageList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_USAGE_COMPANY_LIST', { searchParamsList: params });
    const data = resp as Array<IUsageData>;
    const ranks =
        data
            .sort((a: IUsageData, b: IUsageData) => Number(b.volume) - Number(a.volume))
            .filter((_: ICompanyUsage, index: number) => index < appConfig.top10) || [];
    setDashboardCompanyUsageList(ranks);
}

// ------------ TelecomUsage ------------
function setDashboardTelecomUsageList(data: Array<ITelecomUsage>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setTelecomUsageList(data);
}

export async function getDashboardTelecomUsageList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_USAGE_TELECOM_LIST', { searchParamsList: params });
    const data = resp as Array<IUsageData>;
    const ranks = data.sort((a: IUsageData, b: IUsageData) => Number(b.volume) - Number(a.volume)) || [];

    setDashboardTelecomUsageList(ranks);
}

// ------------ ZoneUsage ------------
function setDashboardZoneUsageList(data: Array<IZoneUsage>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setZoneUsageList(data);
}

export async function getDashboardZoneUsageList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_USAGE_ZONE_LIST', { searchParamsList: params });
    const data = resp as Array<IUsageData>;
    const ranks = data.sort((a: IUsageData, b: IUsageData) => Number(b.volume) - Number(a.volume)) || [];
    setDashboardZoneUsageList(ranks);
}

// ------------ CompanyRevenue ------------
function setDashboardCompanyRevenueList(data: Array<ICompanyRevenueItem>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setCompaniesRevenueList(data);
}

export async function getDashboardCompanyRevenueList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_REVENUE_COMPANY_LIST', {
        searchParamsList: params,
    });
    const data: Array<ICompanyRevenueItem> = mapRank2RevenueItem(resp as Array<IRankCompanyRevenueItemAPI>);
    setDashboardCompanyRevenueList(data);
}

function setDashboardCardSetRevenueList(data: Array<ICardSetRevenueItem>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setCardSetRevenueList(data);
}

export async function getDashboardCardSetRevenueList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_REVENUE_CARD_SET_LIST', {
        searchParamsList: params,
    });
    const data: Array<ICardSetRevenueItem> = mapRank2RevenueItem(resp as Array<IRankCardSetRevenueItemAPI>);
    setDashboardCardSetRevenueList(data);
}

// ------------ Cards Revenue ------------
function setDashboardCardsRevenueList(data: Array<ICardsRevenueItem>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setCardsRevenueList(data);
}

export async function getDashboardCardsRevenueList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_REVENUE_CARDS_LIST', { searchParamsList: params });
    const data: Array<ICardsRevenueItem> = mapRank2RevenueItem(resp as Array<IRankCardRevenueItemAPI>);
    setDashboardCardsRevenueList(data);
}

// ------------ EditPurchaseZoneRate Revenue ------------
function setDashboardCountryRevenueList(data: Array<ICountryRevenueItem>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setCountryRevenueList(data);
}

export async function getDashboardCountryRevenueList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_REVENUE_COUNTRY_LIST', {
        searchParamsList: params,
    });
    const data: Array<ICountryRevenueItem> = mapRank2RevenueItem(resp as Array<IRankCountryRevenueItemAPI>);
    setDashboardCountryRevenueList(data);
}

// ------------ Zone Revenue ------------
function setDashboardZoneRevenueList(data: Array<ITimelineZoneItem>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setZoneRevenueList(data);
}

export async function getDashboardZoneRevenueList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_REVENUE_ZONE_LIST', { searchParamsList: params });
    const data: Array<ITimelineZoneItem> = mapRank2RevenueItem(resp as Array<IRankZoneRevenueItemAPI>);
    setDashboardZoneRevenueList(data);
}

// ------------ Operator/Telecom Revenue ------------
function setDashboardTelecomRevenueList(data: Array<IOperatorRevenueItem>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setTelecomRevenueList(data);
}

export async function getDashboardTelecomRevenueList(params: IParamsList): Promise<void> {
    const { resp } = await apiManager.getApi('GET_REVENUE_TELECOM_LIST', {
        searchParamsList: params,
    });
    const data: Array<IOperatorRevenueItem> = mapRank2RevenueItem(resp as Array<IRankOperatorRevenueItemAPI>);
    setDashboardTelecomRevenueList(data);
}

// ------------ TimelineRevenue ------------
function setDashboardTimelineRevenueList(data: Array<ITimelineRevenueItem>): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setTimelineRevenueList(data);
}
export async function getDashboardTimelineCompanyRevenueList(params: IParamsList = {}): Promise<void> {
    const { resp } = await apiManager.getApi('GET_REVENUE_TIMELINE_LIST', {
        searchParamsList: params,
    });
    const data: Array<ITimelineRevenueItem> = mapRank2RevenueItem(resp as Array<IRankTimelineRevenueItemAPI>).sort(
        (a: ITimelineRevenueItem, b: ITimelineRevenueItem) => sortByString(a.startDate, b.startDate),
    );
    setDashboardTimelineRevenueList(data);
}

// ------------ Main ------------

export async function getDashboardRelationSelect(): Promise<void> {
    getCompaniesSelect(true);
    getCatalogsOriginalUnitTypeSelect();
}

export async function getFinanceRelationSelect(params: IParamsList): Promise<void> {
    getCompaniesSelect(true);
    getCardSetsSelect({ companyId: params?.companyId }, true);
    getOperatorsSelect(false);
    getCatalogsOriginalUnitTypeSelect();
}

export function setDashboardType(type: IDashboardType): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setDashboardType(type);
}

export function setDashboardForcedUpdateTime(): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setForcedUpdateTime();
}

export function cleanDashboardStore(): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.cleanStore();
}

export function cleanDashboardRelationSelectAndStore(): void {
    cleanDashboardStore();
    cleanCompaniesStore();
}

export function setDashboardUsageData(dataName: string, value: IUsageMap): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setUsageData(dataName, value);
}

export function setDashboardOverlayView(overlayView: IOverlayView): void {
    const DashboardStoreInstance = DashboardStore.getInstance();
    DashboardStoreInstance.setMapOverlayView(overlayView);
}

export const abortAllDashboardFetch = (ignoreTypes: IGetApiType[] = []) => {
    const usedApiTypes: IGetApiType[] = [
        'GET_COMPANY_STATISTICS',
        'GET_FINANCE_ROLL',
        'GET_REVENUE_CARD_SET_LIST',
        'GET_REVENUE_CARDS_LIST',
        'GET_REVENUE_COMPANY_LIST',
        'GET_REVENUE_COUNTRY_LIST',
        'GET_REVENUE_TELECOM_LIST',
        'GET_REVENUE_TIMELINE_LIST',
        'GET_REVENUE_ZONE_LIST',
        'GET_ACTIVITY_ROLL',
        'GET_USAGE_CARD_SETS_LIST',
        'GET_USAGE_CARDS_LIST',
        'GET_USAGE_COMPANY_LIST',
        'GET_USAGE_COUNTRY_LIST',
        'GET_USAGE_TELECOM_LIST',
        'GET_USAGE_TIMELINE_LIST',
        'GET_USAGE_ZONE_LIST',
    ];
    const types = usedApiTypes.filter((u) => !ignoreTypes.includes(u));
    abortSelectedFetch(types);
};
